import { useState, useCallback, type ChangeEvent } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { Link } from '@tanstack/react-router'
import { api } from 'convex/_generated/api'
import { usePostHog } from '@/hooks/usePostHog'
import { useGlobalAudioPlayer } from '@/hooks/useGlobalAudioPlayer'
import type { Id } from 'convex/_generated/dataModel'

/**
 * Song generation data structure
 */
export interface SongGeneration {
  _id?: string
  type?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  prompt?: string
  lyrics?: string
  model?: string
  audioUrl?: string
  audioHiUrl?: string
  storageId?: Id<'_storage'>
  generatedLyrics?: string
  temporItemIds?: string[]
  temporRequestId?: string
  error?: string
  _creationTime: number
  title?: string
  tags?: string
}

interface SongCardProps {
  generation: SongGeneration
  className?: string
}

/**
 * Reusable SongCard component with Howler.js audio player
 * 
 * Features:
 * - Play/pause/stop controls
 * - Speed controls (0.5x, 0.75x, 1x, 1.25x, 1.5x, 2x)
 * - Progress tracking with seek
 * - Details toggle
 * - Status-based styling
 * 
 * @component
 */
export function SongCard({ generation, className = '' }: SongCardProps) {
  const [showDetails, setShowDetails] = useState(false)
  const { trackEvent } = usePostHog()

  // Use global audio player (single Howl instance with Redux state)
  const {
    isPlaying: globalIsPlaying,
    duration: globalDuration,
    currentTime: globalCurrentTime,
    playbackRate: globalPlaybackRate,
    play,
    pause,
    stop,
    seek,
    changeRate,
    isCurrentSong,
  } = useGlobalAudioPlayer()

  // Check if this card's song is the currently playing one
  const isThisSongPlaying = generation._id ? isCurrentSong(generation._id) : false
  const isPlaying = isThisSongPlaying && globalIsPlaying
  const duration = isThisSongPlaying ? globalDuration : 0
  const currentTime = isThisSongPlaying ? globalCurrentTime : 0
  const playbackRate = isThisSongPlaying ? globalPlaybackRate : 1.0

  // Get temporItems storage URLs (MP3 from permanent Convex storage)
  const itemUrls = useConvexQuery(
    api.tempolor.getItemStorageUrls,
    generation._id ? { generationId: generation._id as Id<'temporGenerations'> } : 'skip'
  )

  // Get flashcard count for this song
  const flashcardCount = useConvexQuery(
    api.flashcards.countFlashcardsBySong,
    generation.type === 'song' && generation._id
      ? { generationId: generation._id as Id<'temporGenerations'> }
      : 'skip'
  )

  // Use MP3 URL from temporItems storage, fallback to generation.audioUrl for legacy songs
  const effectiveAudioUrl = itemUrls?.mp3Url ?? generation.audioUrl ?? null

  const statusColors = {
    pending: 'border-yellow-500 text-yellow-500',
    processing: 'border-trail-tan text-trail-tan',
    completed: 'border-terminal-green text-terminal-green',
    failed: 'border-red-500 text-red-500',
  }

  const statusColor = statusColors[generation.status] || 'border-sky-blue text-sky-blue'

  // Event handlers for global audio player
  const togglePlayPause = useCallback(() => {
    if (!effectiveAudioUrl || generation.status !== 'completed' || !generation._id) {
      console.warn('[SongCard] Cannot play audio:', {
        hasAudioUrl: !!effectiveAudioUrl,
        status: generation.status,
        hasId: !!generation._id,
      })
      return
    }

    if (isPlaying) {
      pause()
    } else {
      // Track playback event when starting
      const onTrack = () => {
        trackEvent('song_played', {
          songType: generation.type === 'instrumental' ? 'instrumental' : 'song',
          model: generation.model,
          generationId: generation._id,
        })
      }
      play(generation._id, effectiveAudioUrl, onTrack)
    }
  }, [effectiveAudioUrl, generation.status, generation._id, generation.type, generation.model, isPlaying, pause, play, trackEvent])

  const handleStop = useCallback(() => {
    stop()
  }, [stop])

  const handleSeek = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const seekTime = parseFloat(e.target.value)
    seek(seekTime)
  }, [seek])

  const handleSpeedChange = useCallback((rate: number) => {
    changeRate(rate)
  }, [changeRate])

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }, [])

  const speedOptions = [
    { rate: 0.5, label: '0.5x' },
    { rate: 0.75, label: '0.75x' },
    { rate: 1.0, label: '1x' },
    { rate: 1.25, label: '1.25x' },
    { rate: 1.5, label: '1.5x' },
    { rate: 2.0, label: '2x' },
  ]

  return (
    <div className={`terminal-border p-4 bg-black ${statusColor} ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 border-2 font-bold font-mono text-xs ${statusColor}`}>
            {generation.status.toUpperCase()}
          </span>
          <span className="text-trail-tan text-xs font-mono">
            {new Date(generation._creationTime).toLocaleString()}
          </span>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sky-blue font-mono text-sm font-bold hover:text-terminal-green transition-all"
          type="button"
          aria-label={showDetails ? 'Hide details' : 'Show details'}
        >
          {showDetails ? '▼ HIDE' : '► DETAILS'}
        </button>
      </div>

      {/* Title (if available) */}
      {generation.title && (
        <div className="mb-3">
          <div className="text-sky-blue font-mono font-bold text-xs mb-1">[TITLE]:</div>
          <div className="text-white font-mono text-xs">{generation.title}</div>
        </div>
      )}

      {/* Prompt/Theme */}
      {generation.prompt && (
        <div className="mb-3">
          <div className="text-sky-blue font-mono font-bold text-xs mb-1">[THEME]:</div>
          <div className="text-white font-mono text-xs bg-black border border-sky-blue p-2">
            {generation.prompt}
          </div>
        </div>
      )}

      {/* Tags (if available) */}
      {generation.tags && (
        <div className="mb-3">
          <div className="text-sky-blue font-mono font-bold text-xs mb-1">[TAGS]:</div>
          <div className="text-trail-tan font-mono text-xs">{generation.tags}</div>
        </div>
      )}

      {/* Error Message */}
      {generation.error && (
        <div className="mb-3 bg-red-900 border-2 border-red-500 p-3">
          <div className="text-red-300 font-mono font-bold text-xs mb-1">[ERROR]:</div>
          <div className="text-red-200 font-mono text-xs">{generation.error}</div>
        </div>
      )}

      {/* Audio Player */}
      {generation.status === 'completed' && effectiveAudioUrl ? (
        <div className="mb-3 bg-terminal-green border-2 border-terminal-green p-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {!isPlaying ? (
              <button
                onClick={togglePlayPause}
                className="px-4 py-2 bg-sky-blue text-white font-bold font-mono text-sm border-2 border-sky-blue hover:bg-black hover:text-sky-blue transition-all"
                type="button"
                aria-label="Play"
              >
                ▶ PLAY
              </button>
            ) : (
              <button
                onClick={togglePlayPause}
                className="px-4 py-2 bg-white text-black font-bold font-mono text-sm border-2 border-white hover:bg-black hover:text-white transition-all"
                type="button"
                aria-label="Pause"
              >
                ⏸ PAUSE
              </button>
            )}
            <button
              onClick={handleStop}
              className="px-4 py-2 bg-red-500 text-white font-bold font-mono text-sm border-2 border-red-500 hover:bg-black hover:text-red-500 transition-all"
              type="button"
              aria-label="Stop"
            >
              ■ STOP
            </button>
            <span className="text-black font-mono text-xs font-bold">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          
          {/* Speed Controls */}
          <div className="mb-2">
            <div className="text-black font-mono font-bold text-xs mb-1">[SPEED]:</div>
            <div className="flex flex-wrap gap-1">
              {speedOptions.map((option) => (
                <button
                  key={option.rate}
                  onClick={() => handleSpeedChange(option.rate)}
                  className={`px-2 py-1 font-mono text-xs border-2 transition-all ${
                    playbackRate === option.rate
                      ? 'bg-black text-terminal-green border-black'
                      : 'bg-white text-black border-black hover:bg-sky-blue hover:text-white'
                  }`}
                  type="button"
                  aria-label={`Set speed to ${option.label}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progress Bar */}
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
            className="w-full accent-sky-blue"
            aria-label="Seek audio"
          />
        </div>
      ) : generation.status === 'completed' ? (
        <div className="mb-3 bg-yellow-900 border-2 border-yellow-500 p-3">
          <p className="text-yellow-200 font-mono text-xs">
            ⚠️ Audio URL missing - cannot play audio
          </p>
          <p className="text-yellow-300 font-mono text-xs mt-1">
            Status: {generation.status} | audioUrl: {generation.audioUrl ? 'present' : 'missing'}
          </p>
        </div>
      ) : null}

      {/* Details Section */}
      {showDetails && (
        <div className="mt-3 space-y-3">
          {/* Lyrics */}
          {generation.lyrics && (
            <div>
              <div className="text-sky-blue font-mono font-bold text-xs mb-1">[LYRICS]:</div>
              <div className="text-white font-mono text-xs bg-black border border-sky-blue p-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
                {generation.lyrics}
              </div>
            </div>
          )}

          {/* Generated Lyrics (if different) */}
          {generation.generatedLyrics && (
            <div>
              <div className="text-terminal-green font-mono font-bold text-xs mb-1">[AI GENERATED LYRICS]:</div>
              <div className="text-white font-mono text-xs bg-black border border-terminal-green p-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
                {generation.generatedLyrics}
              </div>
            </div>
          )}

          {/* Model */}
          {generation.model && (
            <div>
              <div className="text-sky-blue font-mono font-bold text-xs mb-1">[MODEL]:</div>
              <div className="text-trail-tan font-mono text-xs">{generation.model}</div>
            </div>
          )}

          {/* Request ID */}
          {generation.temporRequestId && (
            <div>
              <div className="text-sky-blue font-mono font-bold text-xs mb-1">[REQUEST ID]:</div>
              <div className="text-trail-tan font-mono text-xs break-all">{generation.temporRequestId}</div>
            </div>
          )}
        </div>
      )}

      {/* Flashcard Association Section */}
      {generation.type === 'song' && generation._id && (
        <div className="mt-4 pt-4 border-t border-sky-blue/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sky-blue font-mono font-bold text-xs">
                [FLASHCARDS]:
              </span>
              {flashcardCount !== undefined && (
                <span className="px-2 py-1 bg-sky-blue/20 text-terminal-green font-mono text-xs font-semibold rounded border border-terminal-green">
                  {flashcardCount} created
                </span>
              )}
            </div>

            <Link
              to="/app/songs/$generationId/create-flashcards"
              params={{ generationId: generation._id }}
              className="px-3 py-1.5 bg-sky-blue/10 hover:bg-sky-blue/20 text-sky-blue border border-sky-blue font-mono text-xs font-bold rounded transition-colors"
            >
              {flashcardCount && flashcardCount > 0
                ? '[VIEW FLASHCARDS]'
                : '[+ CREATE FLASHCARDS]'}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

